// Story 10.1 — Overview del bot.
// Story 12.4 — Mounts EvolutionConnectionPanel for evolution-go bots.
// Story 13.x cleanup — Completa la vista que el placeholder original
// remitía a "siguientes stories del Epic 10": stats today + acceso rápido
// a editor, conversaciones, analytics, settings, y el panel de Evolution.

import { Link, useParams } from '@tanstack/react-router';
import {
  BarChart3,
  FileCode,
  MessageSquare,
  Pause,
  Play,
  Settings as SettingsIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Can } from '@/components/auth/Can';
import { ActiveSessionsPanel } from '@/components/bots/ActiveSessionsPanel';
import { BotMetric } from '@/components/bots/BotMetric';
import { BotStatusBadge } from '@/components/bots/BotStatusBadge';
import { CredentialsPanel } from '@/components/bots/CredentialsPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EvolutionConnectionPanel } from '@/components/whatsapp-connection/EvolutionConnectionPanel';
import { useBotStats } from '@/hooks/queries/use-bot-stats';
import { useBot, useUpdateBotStatus } from '@/hooks/queries/use-bots';
import { cn } from '@/lib/utils';

interface QuickLinkProps {
  to: string;
  params: Record<string, unknown>;
  label: string;
  description: string;
  icon: React.ReactNode;
}

function QuickLink({ to, params, label, description, icon }: QuickLinkProps): React.ReactElement {
  return (
    <Link
      // biome-ignore lint/suspicious/noExplicitAny: TanStack Router param typing relajado.
      to={to as any}
      params={params as never}
      className={cn(
        'flex items-start gap-3 rounded-md border border-border bg-card p-4 transition-colors',
        'hover:border-primary/40 hover:bg-primary/5',
      )}
    >
      <div className="rounded-md bg-primary/10 p-2 text-primary">{icon}</div>
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </div>
    </Link>
  );
}

export function BotPage(): React.ReactElement {
  const params = useParams({ strict: false }) as { botId?: string };
  const botId = Number(params.botId);
  const enabled = Number.isFinite(botId);
  const { data, isLoading, isError } = useBot(botId, enabled);
  const stats = useBotStats(botId, 'today');
  const updateStatus = useUpdateBotStatus(botId);

  function handleSetStatus(next: 'active' | 'inactive'): void {
    if (next === 'inactive') {
      if (!confirm('¿Pausar el bot? Dejará de procesar mensajes hasta reactivarlo.')) return;
    }
    updateStatus.mutate(next, {
      onSuccess: () => {
        toast.success(
          next === 'active' ? 'Bot activado — empezará a procesar mensajes.' : 'Bot pausado.',
        );
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Error cambiando estado del bot');
      },
    });
  }

  if (isLoading) return <p className="text-sm text-muted-foreground">Cargando bot…</p>;
  if (isError || !data) return <p className="text-sm text-destructive">Bot no encontrado.</p>;

  const provider =
    (((data.config as Record<string, unknown>)?.whatsapp as Record<string, unknown> | undefined)
      ?.provider as string | undefined) ?? 'cloud-api';

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{data.name}</h1>
          <div className="mt-1 flex items-center gap-2">
            <BotStatusBadge status={data.status} />
            <span className="text-sm text-muted-foreground">Proveedor: {provider}</span>
          </div>
        </div>
        {/* Story 16.2 — acciones según el rol en la organización del bot. */}
        <Can capability="edit_bot" botId={data.id}>
          <div className="flex gap-2">
            {data.status === 'active' ? (
              <Button
                variant="outline"
                onClick={() => handleSetStatus('inactive')}
                disabled={updateStatus.isPending}
              >
                <Pause className="h-4 w-4" />
                Pausar
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => handleSetStatus('active')}
                disabled={updateStatus.isPending}
              >
                <Play className="h-4 w-4" />
                Activar
              </Button>
            )}
          </div>
        </Can>
      </header>

      {data.status === 'inactive' && provider === 'evolution-go' && (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="pt-6">
            <p className="text-sm">
              Este bot está <strong>inactivo</strong>. Configura las credenciales y conecta el QR
              para activarlo.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Hoy</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando métricas…</p>
          ) : stats.isError ? (
            <p className="text-sm text-muted-foreground">No hay métricas disponibles aún.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <BotMetric label={'Conversa\u00ADciones'} value={stats.data?.conversations ?? 0} />
              <BotMetric label="Recibidos" value={stats.data?.messagesReceived ?? 0} />
              <BotMetric label="Enviados" value={stats.data?.messagesSent ?? 0} />
              <BotMetric label="Flows OK" value={stats.data?.flowsCompleted ?? 0} />
            </div>
          )}
        </CardContent>
      </Card>

      {data.organization && (
        <Can capability="manage_credentials" botId={data.id}>
          <CredentialsPanel
            provider={provider as 'cloud-api' | 'evolution-go'}
            organization={data.organization}
          />
        </Can>
      )}

      <Can capability="edit_bot" botId={data.id}>
        <EvolutionConnectionPanel botId={data.id} botName={data.name} botConfig={data.config} />
      </Can>

      <ActiveSessionsPanel botId={data.id} />

      <div>
        <h2 className="mb-3 text-lg font-semibold">Gestión</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <QuickLink
            to="/bots/$botId/conversations"
            params={{ botId: String(data.id) }}
            label="Conversaciones"
            description="Replay de chats y debug en vivo"
            icon={<MessageSquare className="h-4 w-4" aria-hidden />}
          />
          <QuickLink
            to="/bots/$botId/analytics"
            params={{ botId: String(data.id) }}
            label="Analytics"
            description="Funnels, tasas y tendencias"
            icon={<BarChart3 className="h-4 w-4" aria-hidden />}
          />
          <QuickLink
            to="/bots/$botId/editor"
            params={{ botId: String(data.id) }}
            label="Editor"
            description="DSL del bot, flows y validación"
            icon={<FileCode className="h-4 w-4" aria-hidden />}
          />
          <QuickLink
            to="/bots/$botId/settings"
            params={{ botId: String(data.id) }}
            label="Configuración"
            description="Permisos, dominios y opciones"
            icon={<SettingsIcon className="h-4 w-4" aria-hidden />}
          />
        </div>
      </div>
    </div>
  );
}
